import { SlPopup } from "@shoelace-style/shoelace";
import "@shoelace-style/shoelace/dist/components/divider/divider.js";
import "@shoelace-style/shoelace/dist/components/menu-item/menu-item.js";
import "@shoelace-style/shoelace/dist/components/menu-label/menu-label.js";
import "@shoelace-style/shoelace/dist/components/menu/menu.js";
import shoelaceDark from "@shoelace-style/shoelace/dist/themes/dark.css?inline";
import shoelaceLight from "@shoelace-style/shoelace/dist/themes/light.css?inline";

import { LitElement, PropertyValues, css, html, nothing, unsafeCSS } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";

import { type MatchInput } from "..";
import { InputMenuItemState, iconTemplate, sortItems } from "./insert-menu";

@customElement("c-node-insert-submenu")
export class SubMenu extends LitElement {
    @property()
    item: InputMenuItemState;

    @property()
    input: MatchInput;

    @state()
    private subItems: InputMenuItemState["items"];

    @state()
    selection: number = 0;

    @state()
    private darkMode = false;

    private themeObserver?: MutationObserver;

    connectedCallback() {
        super.connectedCallback();
        this.syncDarkMode();
        this.themeObserver = new MutationObserver(() => this.syncDarkMode());
        this.themeObserver.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ["class"],
        });
    }

    disconnectedCallback() {
        this.themeObserver?.disconnect();
        this.themeObserver = undefined;
        super.disconnectedCallback();
    }

    private syncDarkMode() {
        this.darkMode =
            document.documentElement.classList.contains("sl-theme-dark");
    }

    private get currentItem() {
        return this.subItems[this.selection];
    }

    private get currentSelector() {
        return `[data-index="${this.selection}"]`;
    }

    private get currentDom() {
        return this.renderRoot.querySelector(this.currentSelector);
    }

    private get nestedSubmenuPopup() {
        return this.currentDom?.shadowRoot?.querySelector<SlPopup>("sl-popup");
    }

    private get nestedSubmenu() {
        const submenu = this.currentDom?.querySelector<SubMenu>(
            "c-node-insert-submenu"
        );
        return submenu instanceof SubMenu ? submenu : null;
    }

    down() {
        if (this.nestedSubmenuPopup?.active) {
            this.nestedSubmenu?.down();
            return;
        }
        this.selection = (this.selection + 1) % this.subItems.length;
    }

    up() {
        if (this.nestedSubmenuPopup?.active) {
            this.nestedSubmenu?.up();
            return;
        }
        this.selection = this.selection - 1;
        if (this.selection < 0) {
            this.selection = this.subItems.length - 1;
        }
    }

    private openNestedSubmenu() {
        if (
            this.currentItem?.type === "dropdown" &&
            this.nestedSubmenuPopup &&
            !this.nestedSubmenuPopup.active
        ) {
            this.closeAllNestedSubmenus();
            this.nestedSubmenuPopup.active = true;
            this.nestedSubmenuPopup.focus();
            return true;
        }
        return false;
    }

    openSubmenu() {
        if (this.nestedSubmenuPopup?.active) {
            return this.nestedSubmenu?.openSubmenu() || false;
        }
        return this.openNestedSubmenu();
    }

    closeSubmenu() {
        if (this.nestedSubmenuPopup?.active) {
            if (this.nestedSubmenu?.closeSubmenu()) {
                return true;
            }
            this.nestedSubmenuPopup.active = false;
            return true;
        }
        return false;
    }

    private closeAllNestedSubmenus() {
        this.renderRoot.querySelectorAll("sl-menu-item").forEach((element) => {
            const popup =
                element.shadowRoot?.querySelector<SlPopup>("sl-popup");
            if (popup) {
                popup.active = false;
            }
        });
    }

    runSelection(event: Event) {
        if (this.currentItem?.type === "dropdown") {
            if (this.nestedSubmenuPopup?.active) {
                this.nestedSubmenu?.runSelection(event);
            } else {
                this.openNestedSubmenu();
            }
            return;
        }
        this.currentItem?.run(event, this.input);
    }

    async willUpdate(changedProperties: PropertyValues<this>) {
        if (changedProperties.has("item")) {
            if (this.item?.getItems) {
                this.subItems = sortItems(await this.item.getItems());
            } else if (this.item?.items) {
                this.subItems = sortItems(this.item.items);
            } else {
                this.subItems = [];
            }
        }
    }

    render() {
        return html`<sl-menu
            class=${this.darkMode ? "sl-theme-dark" : "sl-theme-light"}
        >
            ${this.subItems
                ? this.subItems.map(
                      (menuItem, index) => html`
                          <sl-menu-item
                              ?disabled=${menuItem.disabled}
                              @mouseenter=${() => {
                                  this.selection = index;
                                  this.openNestedSubmenu();
                              }}
                              @focus=${() => {
                                  this.selection = index;
                              }}
                              @click=${(e: MouseEvent) => {
                                  if (menuItem.type === "dropdown") {
                                      this.openNestedSubmenu();
                                  } else if (menuItem.run) {
                                      menuItem.run(e, this.input);
                                  }
                              }}
                              class=${classMap({
                                  selected: index === this.selection,
                              })}
                              title=${menuItem.label}
                              data-index=${index}
                          >
                              ${menuItem?.icon
                                  ? iconTemplate({
                                        ...menuItem.icon,
                                        slot: "prefix",
                                    })
                                  : nothing}
                              ${menuItem?.label || ""}
                              ${menuItem.type === "dropdown"
                                  ? html`
                                        <c-node-insert-submenu
                                            slot="submenu"
                                            .input=${this.input}
                                            .item=${menuItem}
                                        ></c-node-insert-submenu>
                                    `
                                  : nothing}
                          </sl-menu-item>
                      `
                  )
                : html`<div>Loading...</div>`}
        </sl-menu>`;
    }

    static styles = [
        unsafeCSS(shoelaceLight),
        unsafeCSS(shoelaceDark),
        css`
            .selected:not([disabled])::part(base) {
                background: var(--stekz-secondary-background);
                color: var(--text-color);
            }

            .selected[disabled]::part(base) {
                background-color: var(--sl-color-neutral-100);
                color: var(--text-color);
            }
        `,
    ];
}
